import React, { useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { Alert } from 'react-bootstrap';
import { Field } from '../../../../components/Form';
import { constants, actions } from '../../../../redux/windshieldRepairabilityProfiles';
import { Loader, ConfirmationModal, Checkbox, Combobox } from '../../../../components';
import { comboTypes } from '../../../../components/Combobox/Combobox';
import { authenticationService } from '../../../../services';
import { constants as sessionConstants } from '../../../../redux/session';
import WindshieldRepairabilityProfileAreas from './WindshieldRepairabilityProfileAreas';
import { makeDelete, makePatch } from '../../../../api/ajax';

// The API prefixes validation errors with the offending field name, e.g.
// "Name: HTML or markup is not allowed in this field." Map that prefix (lower-cased)
// to the Formik field so the error is shown under the correct input.
const FIELD_BY_ERROR_PREFIX = {
  name: 'name',
  windshieldsafetyareafromcornersincm: 'windshieldSafetyAreaFromCornersInCm',
  safetyareaaroundthechipincm: 'safetyAreaAroundTheChipInCm',
  maximumrepairabledamages: 'maximumRepairableDamages',
  repairablechipsizeincm: 'repairableChipSizeInCm',
  repairablecracksizeincm: 'repairableCrackSizeInCm',
};

const repairabilityReviewTriggerItems = Object.keys(constants.REPAIRABILITY_REVIEW_TRIGGER_TYPE).map((key) => {
  const triggerType = constants.REPAIRABILITY_REVIEW_TRIGGER_TYPE[key];
  return {
    id: triggerType,
    name: constants.REPAIRABILITY_REVIEW_TRIGGER_TYPE_NAME[triggerType]
  };
});

const WindshieldRepairabilityProfile = ({ windshieldRepairabilityProfile }) => {
  const [
    windshieldSafetyAreaFromCornersInCm,
    setWindshieldSafetyAreaFromCornersInCm
  ] = useState(windshieldRepairabilityProfile.windshieldSafetyAreaFromCornersInCm);
  const [
    safetyAreaAroundTheChipInCm,
    setSafetyAreaAroundTheChipInCm
  ] = useState(windshieldRepairabilityProfile.safetyAreaAroundTheChipInCm);
  const [driverFieldOfView, setDriverFieldOfView] = useState(windshieldRepairabilityProfile.driverFieldOfView);
  const [driverFieldOfViewLocation, setDriverFieldOfViewLocation] = useState(windshieldRepairabilityProfile.driverFieldOfViewLocation);
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [hasAdasSystems, setHasAdasSystems] = useState(windshieldRepairabilityProfile.adasFieldOfViewMask?.length > 0);
  const [noActionRequiredRepairDecisionEnabled, setNoActionRequiredRepairDecisionEnabled] = useState(
    windshieldRepairabilityProfile.noActionRequiredRepairDecisionEnabled
  );
  const [crackRepairEnabled, setCrackRepairEnabled] = useState(windshieldRepairabilityProfile.crackRepairEnabled);
  const [needReviewNotificationEnabled, setNeedReviewNotificationEnabled] = useState(windshieldRepairabilityProfile.needReviewNotificationEnabled);

  const [driverFieldOfViewMask, setDriverFieldOfViewMask] = useState(windshieldRepairabilityProfile.driverFieldOfViewMask);
  const [adasFieldOfViewMask, setAdasFieldOfViewMask] = useState(windshieldRepairabilityProfile.adasFieldOfViewMask);
  const [selectedRepairabilityReviewTriggers, setSelectedRepairabilityReviewTriggers] = useState(() => {
    const triggers = windshieldRepairabilityProfile.repairabilityReviewTriggers || [];
    return repairabilityReviewTriggerItems.filter((item) => triggers.includes(item.id));
  });

  const repairabilityReviewTriggersRef = useRef();

  const dispatch = useDispatch();
  const { hasPermission } = authenticationService;

  const {
    DELETE_WINDSHIELD_REPAIRABILITY_PROFILE, EDIT_WINDSHIELD_REPAIRABILITY_PROFILE
  } = sessionConstants.PERMISSION_TYPES;

  const deleteWindshieldRepairabilityProfile = () => {
    makeDelete(`windshieldRepairProfiles/${windshieldRepairabilityProfile.id}`).subscribe(() => {
      dispatch(actions.deleteWindshieldRepairabilityProfileSuccess(windshieldRepairabilityProfile.id));
    });
  };

  const handleSubmit = (formValues, { setSubmitting, setStatus, setFieldError }, hasPermission) => {
    if (!hasPermission(EDIT_WINDSHIELD_REPAIRABILITY_PROFILE)) return;
    setErrorMessage(null);
    const updatedWindshieldRepairabilityProfile = {
      ...formValues,
      driverFieldOfView: parseInt(driverFieldOfView), // eslint-disable-line radix
      driverFieldOfViewLocation: parseInt(driverFieldOfViewLocation), // eslint-disable-line radix
      driverFieldOfViewMask,
      adasFieldOfViewMask,
      repairabilityReviewTriggers: selectedRepairabilityReviewTriggers.map((item) => item.id)
    };
    makePatch(`windshieldRepairProfiles/${windshieldRepairabilityProfile.id}`, updatedWindshieldRepairabilityProfile)
      .subscribe(
        ({ response }) => {
          dispatch(actions.editWindshieldRepairabilityProfileSiccess(response));
          setStatus(`Changes saved for '${updatedWindshieldRepairabilityProfile.name}'`);
          setSubmitting(false);
        },
        (error) => {
          setSubmitting(false);
          const message = error?.response?.error || 'Failed to save changes. Please try again.';
          const prefix = typeof message === 'string' ? message.split(':')[0].trim().toLowerCase() : '';
          const field = FIELD_BY_ERROR_PREFIX[prefix];

          // Route the error to the field it belongs to; otherwise show it as a general error.
          if (field) {
            setFieldError(field, message);
          } else {
            setErrorMessage(message);
          }
        },
      );
  };

  return (
    <div className="d-flex">
      <div className="d-flex flex-column">
        <WindshieldRepairabilityProfileAreas
          repairAreaCm={windshieldSafetyAreaFromCornersInCm}
          safetyAreaAroundTheChipInCm={safetyAreaAroundTheChipInCm}
          driverFieldOfViewMask={driverFieldOfViewMask}
          adasFieldOfViewMask={adasFieldOfViewMask}
          setDriverFieldOfViewMask={setDriverFieldOfViewMask}
          setAdasFieldOfViewMask={setAdasFieldOfViewMask}
          driverFieldOfView={Number(driverFieldOfView)}
          driverFieldOfViewLocation={Number(driverFieldOfViewLocation)}
        />
        <div className="d-flex justify-content-center">
          <span className="mt-2">To change the size of the areas, click on them</span>
        </div>
      </div>
      <div className="ml-4 w-100">
        <h5>Settings</h5>
        <Formik
          initialValues={{
            name: windshieldRepairabilityProfile.name,
            windshieldSafetyAreaFromCornersInCm: windshieldRepairabilityProfile.windshieldSafetyAreaFromCornersInCm,
            safetyAreaAroundTheChipInCm: windshieldRepairabilityProfile.safetyAreaAroundTheChipInCm,
            maximumRepairableDamages: windshieldRepairabilityProfile.maximumRepairableDamages,
            hasAdasSystems,
            repairableChipSizeInCm: windshieldRepairabilityProfile.repairableChipSizeInCm,
            repairableCrackSizeInCm: windshieldRepairabilityProfile.repairableCrackSizeInCm,
            noActionRequiredRepairDecisionEnabled,
            crackRepairEnabled,
            needReviewNotificationEnabled
          }}
          onSubmit={(formValues, formikHelpers) => handleSubmit(formValues, formikHelpers, hasPermission)}
          enableReinitialize
          validationSchema={
            Yup.object().shape({
              name: Yup.string()
                .required('Name is required')
                .max(50, 'Name must not be longer than 50 characters')
                .min(5, 'Name must be at least 5 characters'),
              windshieldSafetyAreaFromCornersInCm: Yup.number()
                .required('Safety area is required')
                .max(30, 'Safety area from corners must not exceed 30cm')
                .min(0, 'Safety area cannot be negative'),
              safetyAreaAroundTheChipInCm: Yup.number()
                .required('Safety area around chips is required')
                .max(30, 'Safety area around chips must not exceed 30cm')
                .min(0, 'Safety area around chips cannot be negative'),
              maximumRepairableDamages: Yup.number()
                .required('Maximum repairable damages is required')
                .max(20, 'Maximum repairable damages must not exceed 20')
                .min(0, 'Maximum repairable damages cannot be negative'),
              repairableChipSizeInCm: Yup.number()
                .required('Repairable chip size is required')
                .min(0, 'Repairable chip size cannot be negative'),
              repairableCrackSizeInCm: Yup.number()
                .required('Repairable crack size is required')
                .min(0, 'Repairable crack size cannot be negative')
            })
          }
        >
          {
            ({ errors, touched, isSubmitting, status, setStatus, handleChange }) => (
              <Form>
                <div className="row">
                  <div className="col-12 col-sm-12">
                    {status && (
                      <Alert variant="success" dismissible onClick={() => setStatus(null)}>
                        {status}
                      </Alert>
                    )}
                    {errorMessage && (
                      <Alert variant="danger" dismissible onClick={() => setErrorMessage(null)}>
                        {errorMessage}
                      </Alert>
                    )}
                  </div>
                </div>
                <div className="row">
                  <div className="col-12 col-sm-12">
                    <Field
                      min="5"
                      max="50"
                      type="text"
                      name="name"
                      label="Name"
                      touched={touched.name}
                      errors={errors.name}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-12 col-sm-12">
                    <Field
                      type="number"
                      min="0"
                      max="30"
                      name="windshieldSafetyAreaFromCornersInCm"
                      label="Windshield safety area from corners in cm"
                      touched={touched.windshieldSafetyAreaFromCornersInCm}
                      errors={errors.windshieldSafetyAreaFromCornersInCm}
                      onChange={(e) => {
                        if (!isNaN(e.target.value) && Number(e.target.value) >= 0 && Number(e.target.value) <= 30) {
                          setWindshieldSafetyAreaFromCornersInCm(Number(e.target.value));
                        }
                        handleChange(e);
                      }}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-12 col-sm-12">
                    <Field
                      type="number"
                      min="0"
                      max="30"
                      name="safetyAreaAroundTheChipInCm"
                      label="Safety area around the chip in cm"
                      touched={touched.safetyAreaAroundTheChipInCm}
                      errors={errors.safetyAreaAroundTheChipInCm}
                      onChange={(e) => {
                        if (!isNaN(e.target.value) && Number(e.target.value) >= 0 && Number(e.target.value) <= 30) {
                          setSafetyAreaAroundTheChipInCm(Number(e.target.value));
                        }
                        handleChange(e);
                      }}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-12 col-sm-12">
                    <Field
                      type="number"
                      min="0"
                      max="20"
                      name="maximumRepairableDamages"
                      label="Maximum repairable damages"
                      touched={touched.maximumRepairableDamages}
                      errors={errors.maximumRepairableDamages}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-12 col-sm-12">
                    <Field
                      type="number"
                      min="0"
                      step="0.01"
                      name="repairableChipSizeInCm"
                      label="Repairable chip size in cm"
                      touched={touched.repairableChipSizeInCm}
                      errors={errors.repairableChipSizeInCm}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-12 col-sm-12">
                    <Field
                      type="number"
                      min="0"
                      step="0.01"
                      name="repairableCrackSizeInCm"
                      label="Repairable crack size in cm"
                      touched={touched.repairableCrackSizeInCm}
                      errors={errors.repairableCrackSizeInCm}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-12 col-sm-12">
                    <div className="form-group">
                      <label className="mb-0 mt-1" htmlFor="driverFieldOfView">
                        Driver field of view
                      </label>
                      <select
                        className="form-control"
                        value={driverFieldOfView}
                        onChange={(event) => setDriverFieldOfView(event.target.value)}
                        name="driverFieldOfView"
                        id="driverFieldOfView"
                      >
                        <option value={constants.DRIVER_FIELD_OF_VIEW.OFF}>
                          {constants.DRIVER_FIELD_OF_VIEW_NAME[constants.DRIVER_FIELD_OF_VIEW.OFF]}
                        </option>
                        <option value={constants.DRIVER_FIELD_OF_VIEW.WARNING}>
                          {constants.DRIVER_FIELD_OF_VIEW_NAME[constants.DRIVER_FIELD_OF_VIEW.WARNING]}
                        </option>
                        <option value={constants.DRIVER_FIELD_OF_VIEW.BLOCKER}>
                          {constants.DRIVER_FIELD_OF_VIEW_NAME[constants.DRIVER_FIELD_OF_VIEW.BLOCKER]}
                        </option>
                      </select>
                    </div>
                  </div>
                </div>
                {
                  Number(driverFieldOfView) !== constants.DRIVER_FIELD_OF_VIEW.OFF && (
                    <div className="row">
                      <div className="col-12 col-sm-12">
                        <div className="form-group">
                          <label className="mb-0 mt-1" htmlFor="driverFieldOfViewLocation">
                            Driver field of view location
                          </label>
                          <select
                            className="form-control"
                            value={driverFieldOfViewLocation}
                            onChange={(event) => {
                              if (Number(event.target.value) === constants.DRIVER_FIELD_OF_VIEW_LOCATION.RIGHT) {
                                setDriverFieldOfViewMask(constants.DEFAULT_DRIVER_FOV_RIGHT);
                              } else if (Number(event.target.value) === constants.DRIVER_FIELD_OF_VIEW_LOCATION.CENTER) {
                                setDriverFieldOfViewMask(constants.DEFAULT_DRIVER_FOV_CENTER);
                              } else if (Number(event.target.value) === constants.DRIVER_FIELD_OF_VIEW_LOCATION.LEFT) {
                                setDriverFieldOfViewMask(constants.DEFAULT_DRIVER_FOV_LEFT);
                              }

                              setDriverFieldOfViewLocation(event.target.value);
                            }}
                            name="driverFieldOfViewLocation"
                            id="driverFieldOfViewLocation"
                          >
                            <option value={constants.DRIVER_FIELD_OF_VIEW_LOCATION.RIGHT}>
                              {constants.DRIVER_FIELD_OF_VIEW_LOCATION_NAME[constants.DRIVER_FIELD_OF_VIEW_LOCATION.RIGHT]}
                            </option>
                            <option value={constants.DRIVER_FIELD_OF_VIEW_LOCATION.CENTER}>
                              {constants.DRIVER_FIELD_OF_VIEW_LOCATION_NAME[constants.DRIVER_FIELD_OF_VIEW_LOCATION.CENTER]}
                            </option>
                            <option value={constants.DRIVER_FIELD_OF_VIEW_LOCATION.LEFT}>
                              {constants.DRIVER_FIELD_OF_VIEW_LOCATION_NAME[constants.DRIVER_FIELD_OF_VIEW_LOCATION.LEFT]}
                            </option>
                          </select>
                        </div>
                      </div>
                    </div>
                  )
                }
                <div className="row">
                  <div className="col-6 col-sm-6">
                    <div className="mt-3">
                      <Checkbox
                        id="hasAdasSystems"
                        label="ADAS systems enabled"
                        name="hasAdasSystems"
                        checked={hasAdasSystems}
                        onClickHandler={() => {
                          if (hasAdasSystems) {
                            setAdasFieldOfViewMask([]);
                          } else {
                            setAdasFieldOfViewMask([constants.DEFAULT_ADAS_FOV]);
                          }
                          setHasAdasSystems(!hasAdasSystems);
                        }}
                      />
                    </div>
                  </div>
                  <div className="col-6 col-sm-6">
                    <div className="mt-3">
                      <Checkbox
                        id="noActionRequiredRepairDecisionEnabled"
                        label="No action required enabled"
                        name="noActionRequiredRepairDecisionEnabled"
                        checked={noActionRequiredRepairDecisionEnabled}
                        onClickHandler={() => setNoActionRequiredRepairDecisionEnabled(!noActionRequiredRepairDecisionEnabled)}
                      />
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-6 col-sm-6">
                    <div className="mt-3">
                      <Checkbox
                        id="crackRepairEnabled"
                        label="Crack repair enabled"
                        name="crackRepairEnabled"
                        checked={crackRepairEnabled}
                        onClickHandler={() => setCrackRepairEnabled(!crackRepairEnabled)}
                      />
                    </div>
                  </div>
                  <div className="col-6 col-sm-6">
                    <div className="mt-3">
                      <Checkbox
                        id="needReviewNotificationEnabled"
                        label="Need review notification enabled"
                        name="needReviewNotificationEnabled"
                        checked={needReviewNotificationEnabled}
                        onClickHandler={() => setNeedReviewNotificationEnabled(!needReviewNotificationEnabled)}
                      />
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-12 col-sm-12">
                    <div className="mb-2">
                      <Combobox
                        type={comboTypes.multiCombo}
                        label="Repairability human review settings"
                        values={repairabilityReviewTriggerItems}
                        initialSelection={selectedRepairabilityReviewTriggers}
                        getSelectedItems={(selectedItems) => setSelectedRepairabilityReviewTriggers(selectedItems)}
                        searchingDisabled={false}
                        ref={repairabilityReviewTriggersRef}
                      />
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-sm-12 text-right mt-2">
                    {hasPermission(DELETE_WINDSHIELD_REPAIRABILITY_PROFILE) && (
                      <button
                        type="button"
                        className="button btn"
                        data-variation="light-blue"
                        onClick={() => setShowConfirmDeleteModal(true)}
                      >
                        Remove
                      </button>
                    )}
                    {hasPermission(EDIT_WINDSHIELD_REPAIRABILITY_PROFILE) && (
                      <button type="submit" className="button btn btn-primary ml-2" disabled={isSubmitting}>
                        {isSubmitting
                          ? (<Loader size="tiny" isRelative noDelay white />)
                          : 'Save'}
                      </button>
                    )}
                  </div>
                </div>
              </Form>
            )
          }
        </Formik>
        <ConfirmationModal
          showModal={showConfirmDeleteModal}
          heading="Delete windshield repairability profile"
          message="Are you sure you wish to delete this windshield repairability profile?"
          onConfirm={() => {
            deleteWindshieldRepairabilityProfile();
            setShowConfirmDeleteModal(false);
          }}
          onCancel={() => setShowConfirmDeleteModal(false)}
          style={{ top: '6.25rem' }}
        />
      </div>
    </div>
  );
};

WindshieldRepairabilityProfile.propTypes = {
  windshieldRepairabilityProfile: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    windshieldSafetyAreaFromCornersInCm: PropTypes.number,
    safetyAreaAroundTheChipInCm: PropTypes.number,
    maximumRepairableDamages: PropTypes.number,
    driverFieldOfView: PropTypes.number,
    driverFieldOfViewLocation: PropTypes.number,
    adasFieldOfViewMask: PropTypes.array,
    driverFieldOfViewMask: PropTypes.array,
    repairableChipSizeInCm: PropTypes.number,
    repairableCrackSizeInCm: PropTypes.number,
    repairabilityReviewTriggers: PropTypes.array,
    noActionRequiredRepairDecisionEnabled: PropTypes.bool,
    crackRepairEnabled: PropTypes.bool,
    needReviewNotificationEnabled: PropTypes.bool,
  }),
};

export default WindshieldRepairabilityProfile;
